/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { Colleague } from "@lemmonhq/types";
import { initials } from "./utils";

type StaffPickerProps = {
  colleagues: Colleague[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
};

/** Toggleable staff chips: tap a person to assign or unassign them. */
export const StaffPicker = defineComponent<StaffPickerProps>({
  name: "TerminalStaffPicker",
  props: ["colleagues", "selectedIds", "onChange"],
  setup(props) {
    const { t } = useLocalization();

    const toggle = (id: number) => {
      const selected = props.selectedIds.includes(id);
      props.onChange(
        selected
          ? props.selectedIds.filter((s) => s !== id)
          : [...props.selectedIds, id],
      );
    };

    return () => (
      <div class="space-y-2">
        <div class="flex items-baseline justify-between">
          <span class="text-sm font-medium text-base-content/80">
            {t("settings.terminals.assigned_staff")}
          </span>
          <span class="text-xs text-base-content/60">
            {props.selectedIds.length} / {props.colleagues.length}
          </span>
        </div>

        {props.colleagues.length === 0 ? (
          <p class="text-sm text-base-content/60">
            {t("settings.terminals.no_staff")}
          </p>
        ) : (
          <div class="flex flex-wrap gap-2">
            {props.colleagues.map((colleague) => {
              const selected = props.selectedIds.includes(colleague.id);
              return (
                <button
                  key={colleague.id}
                  type="button"
                  aria-pressed={selected}
                  class={`flex min-h-11 items-center gap-2 rounded-full border py-1 pl-1 pr-3 text-sm transition-colors ${
                    selected
                      ? "border-primary bg-primary/10 text-base-content"
                      : "border-base-300 text-base-content/70 hover:border-base-content/30"
                  }`}
                  onClick={() => toggle(colleague.id)}
                >
                  <span
                    class={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                      selected
                        ? "bg-primary text-primary-content"
                        : "bg-base-300 text-base-content/70"
                    }`}
                  >
                    {selected ? (
                      <span class="material-symbols-outlined text-base">check</span>
                    ) : (
                      initials(colleague.profile.name)
                    )}
                  </span>
                  {colleague.profile.name}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  },
});
